import * as React from "react";
import { cn } from "../../lib/utils";
import { Badge } from "./badge";
import { CheckIcon } from "lucide-react";

const Steps = React.forwardRef(
  ({ className, current = 0, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("flex items-center justify-between w-full", className)}
        {...props}
      >
        {React.Children.map(children, (child, index) => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child, {
              ...child.props,
              stepIndex: index,
              current,
              isLast: index === React.Children.count(children) - 1,
            });
          }
          return child;
        })}
      </div>
    );
  }
);
Steps.displayName = "Steps";

const Step = React.forwardRef(
  (
    {
      className,
      title,
      description,
      icon,
      stepIndex,
      current,
      isLast,
      ...props
    },
    ref
  ) => {
    const isActive = stepIndex === current;
    const isCompleted = stepIndex < current;

    return (
      <div
        className={cn("flex items-center", !isLast && "flex-1")}
        ref={ref}
        {...props}
      >
        <div className="flex flex-col items-center">
          <div
            className={cn(
              "flex items-center justify-center w-8 h-8 rounded-full transition-colors",
              isCompleted && "bg-primary text-primary-foreground",
              isActive && "bg-destructive text-primary-foreground",
              !isActive && !isCompleted && "border-1 text-muted-foreground"
            )}
          >
            {isCompleted ? (
              <CheckIcon className="w-4 h-4" />
            ) : icon ? (
              <span className="w-4 h-4 flex items-center justify-center">
                {icon}
              </span>
            ) : (
              <span className="text-sm font-medium">{stepIndex + 1}</span>
            )}
          </div>
          {title && (
            <div className="mt-2 text-center">
              <div
                className={cn(
                  "text-sm font-medium",
                  isActive && "text-primary",
                  isCompleted && "text-primary",
                  !isActive && !isCompleted && "text-muted-foreground"
                )}
              >
                {title}
              </div>
              {description && (
                <div className="text-xs text-muted-foreground mt-1">
                  {description}
                </div>
              )}
            </div>
          )}
        </div>
        {!isLast && (
          <div
            className={cn(
              "flex-1 h-px mx-4 transition-colors -mt-6",
              isCompleted ? "bg-primary" : "bg-border"
            )}
          />
        )}
      </div>
    );
  }
);
Step.displayName = "Step";

export { Steps, Step };
